<template>
  <div class="api-doc-page page-container">
    <section class="page-header">
      <h1>日历接入</h1>
      <p>复制接口信息，让 AI 帮你填写日期</p>
    </section>

    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="把下面内容复制给 AI，AI 就能按指定月份 JSON 帮你填写 Excel 日期部分。"
      class="copy-hint"
    />

    <el-form :inline="true" @submit.native.prevent style="margin-bottom: 16px">
      <el-form-item label="年份">
        <el-input-number
          v-model="query.year"
          :min="2020"
          :max="2099"
          controls-position="right"
          size="small"
        />
      </el-form-item>
      <el-form-item label="月份">
        <el-input-number
          v-model="query.month"
          :min="1"
          :max="12"
          controls-position="right"
          size="small"
        />
      </el-form-item>
      <el-form-item>
        <el-button
          type="primary"
          size="small"
          :loading="loading"
          @click="loadCalendar"
        >
          查询并刷新示例
        </el-button>
        <el-button type="success" size="small" @click="copyApiDoc">
          {{ copyStatus || '复制给 AI' }}
        </el-button>
      </el-form-item>
    </el-form>

    <div class="api-doc-content">
      <el-card shadow="never">
        <div slot="header"><strong>接口信息</strong></div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="接口地址">{{ apiDocRequestPath }}</el-descriptions-item>
          <el-descriptions-item label="请求方式">POST</el-descriptions-item>
          <el-descriptions-item label="请求类型">application/x-www-form-urlencoded</el-descriptions-item>
          <el-descriptions-item label="响应格式">JSON</el-descriptions-item>
          <el-descriptions-item label="用途">按年份和月份查询日期类型，供 AI 或 Excel 填表使用。</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <el-card shadow="never">
        <div slot="header"><strong>请求参数</strong></div>
        <el-table :data="apiDocParams" border size="small">
          <el-table-column prop="name" label="参数" width="120" />
          <el-table-column prop="desc" label="说明" />
          <el-table-column prop="required" label="必填" width="80" />
          <el-table-column prop="example" label="当前值" width="120" />
        </el-table>
      </el-card>

      <el-card shadow="never">
        <div slot="header"><strong>字段说明</strong></div>
        <el-table :data="apiDocFields" border size="small">
          <el-table-column prop="field" label="字段" width="140" />
          <el-table-column prop="desc" label="含义" />
        </el-table>
      </el-card>

      <el-card shadow="never">
        <div slot="header"><strong>当前月份响应 JSON</strong></div>
        <p v-if="!calendar" class="api-doc-tip">当前还没有查询结果，先点击上方“查询并刷新示例”按钮。</p>
        <pre class="api-code"><code>{{ apiDocResponseExample }}</code></pre>
      </el-card>
    </div>
  </div>
</template>

<script>
import { queryCalendar } from '../api/calendar'

export default {
  name: 'ApiDocPage',
  data() {
    const now = new Date()
    return {
      query: {
        year: Math.max(now.getFullYear(), 2020),
        month: now.getMonth() + 1
      },
      calendar: null,
      loading: false,
      copyStatus: '',
      apiDocFields: [
        { field: 'date', desc: '完整日期，格式 yyyy-MM-dd' },
        { field: 'dayOfMonth', desc: '当月第几天' },
        { field: 'weekDay', desc: '星期几，1 表示周一，7 表示周日' },
        { field: 'dayType', desc: 'WORKDAY 工作日，WEEKEND 周末，HOLIDAY 节假日' },
        { field: 'dayName', desc: '节假日名称，例如元旦、端午节；非节假日通常为空' }
      ]
    }
  },
  computed: {
    normalizedYear() {
      return this.query.year || 2020
    },
    normalizedMonth() {
      return this.query.month || 1
    },
    apiDocRequestPath() {
      return `/api/toolbox/calendar/page?year=${this.normalizedYear}&month=${this.normalizedMonth}`
    },
    apiDocParams() {
      return [
        { name: 'year', desc: '年份，当前最小支持 2020', required: '是', example: String(this.normalizedYear) },
        { name: 'month', desc: '月份，取值 1-12', required: '是', example: String(this.normalizedMonth) }
      ]
    },
    apiDocResponseExample() {
      const yearMonth = `${this.normalizedYear}-${String(this.normalizedMonth).padStart(2, '0')}`
      const response = {
        code: 200,
        message: '成功',
        data: this.calendar || {
          yearMonth,
          days: [
            {
              date: `${yearMonth}-01`,
              dayOfMonth: 1,
              weekDay: 4,
              dayType: 'HOLIDAY',
              dayName: '元旦'
            }
          ]
        }
      }
      return JSON.stringify(response, null, 2)
    },
    aiCopyContent() {
      return [
        '请根据下面这个日历接口返回的 JSON，帮我填写 Excel 表格中的日期相关字段。',
        '',
        `接口地址：${this.apiDocRequestPath}`,
        '请求方式：POST',
        `请求参数：year=${this.normalizedYear}&month=${this.normalizedMonth}`,
        'dayType 含义：WORKDAY=工作日，WEEKEND=周末，HOLIDAY=节假日。',
        'weekDay 含义：1=周一，2=周二，3=周三，4=周四，5=周五，6=周六，7=周日。',
        'dayName 是节假日名称，非节假日通常为空。',
        '',
        'JSON 数据：',
        this.apiDocResponseExample
      ].join('\n')
    }
  },
  mounted() {
    this.loadCalendar()
  },
  methods: {
    async loadCalendar() {
      if (!this.query.year || !this.query.month) {
        this.$message.warning('请填写年份和月份')
        return
      }
      this.loading = true
      try {
        const { data } = await queryCalendar(this.query.year, this.query.month)
        if (data.code !== 200) {
          throw new Error(data.message || '查询失败')
        }
        this.calendar = data.data
      } catch (error) {
        this.calendar = null
        this.$message.error(this.resolveErrorMessage(error, '查询失败'))
      } finally {
        this.loading = false
      }
    },
    async copyApiDoc() {
      try {
        await this.copyText(this.aiCopyContent)
        this.copyStatus = '已复制'
        this.$message.success('已复制到剪贴板')
        window.setTimeout(() => {
          this.copyStatus = ''
        }, 1600)
      } catch (error) {
        this.$message.error(this.resolveErrorMessage(error, '复制失败，请手动复制 JSON 内容'))
      }
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const successful = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!successful) {
        throw new Error('复制失败，请手动复制 JSON 内容')
      }
    },
    resolveErrorMessage(error, fallback) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error.message || fallback
    }
  }
}
</script>

<style scoped>
.api-doc-page { padding-top: 32px; padding-bottom: 80px; }
.copy-hint { margin: 32px 0 16px; }
.api-doc-content {
  display: grid;
  gap: 16px;
}

.api-doc-content .el-card { background: var(--surface); }
.api-doc-content .el-card .el-card__header { padding: 16px 20px; }
.api-doc-content .el-card .el-card__header strong { color: var(--ink); font-size: 16px; font-weight: 600; }

.api-doc-tip {
  margin: 0 0 8px;
  color: var(--ink-3);
  font-size: 13px;
}

.api-code {
  max-height: 420px;
  margin: 8px 0 0;
  padding: 14px;
  overflow: auto;
  border-radius: var(--r-md);
  background: #1A1B23;
  color: #E8F5F2;
  font-family: "JetBrains Mono", "Fira Code", Consolas, "Courier New", monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre;
}
</style>
